import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { isOneOffZone } from './lib/one-off-sites';
import { DEFAULT_MEMPHIS_LAYERS, EMPTY_MEMPHIS_LAYERS, MEMPHIS_RESEARCH, type MemphisLayers } from './data/memphis-research';
import { DEFAULT_ET_LAYERS, EMPTY_ET_LAYERS, fetchEtResearch, type EtLayers, type EtResearchPayload } from './data/et-research';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { MapView, type OperatorInfo } from './components/MapView';
import { FilterSidebar } from './components/FilterSidebar';
import { GEORGIA_SITE_BOUNDS } from './data/georgia-sites';
import { ParcelDetail } from './components/ParcelDetail';
import { PipelineDetail, type PipelineProperties } from './components/PipelineDetail';
import { CarrierFiberRouteDetail } from './components/CarrierFiberRouteDetail';
import { RailroadDetail } from './components/RailroadDetail';
import { IxpFacilityDetail, type IxpFacilityProperties } from './components/IxpFacilityDetail';
import { WellDetail } from './components/WellDetail';
import { DataCenterDetail, type DataCenterProperties } from './components/DataCenterDetail';
import { LandListingDetail, type LandListingProperties } from './components/LandListingDetail';
import { FloodZoneDetail, type FloodZoneProperties } from './components/FloodZoneDetail';
import { MineSubsidenceDetail, type MineSubsidenceProperties } from './components/MineSubsidenceDetail';
import { AmlSiteDetail, type AmlSiteProperties } from './components/AmlSiteDetail';
import { WildfireDetail, type WildfireProperties } from './components/WildfireDetail';
import { UrbanAreaDetail, type UrbanAreaProperties } from './components/UrbanAreaDetail';
import { SeismicRiskDetail, type SeismicRiskProperties } from './components/SeismicRiskDetail';
import { GasInfraDetail, type GasInfraProperties } from './components/GasInfraDetail';
import { SubstationDetail, type SubstationProperties } from './components/SubstationDetail';
import { AirportDetail, type AirportProperties } from './components/AirportDetail';
import { OsmSwitchyardDetail, type OsmSwitchyardProperties } from './components/OsmSwitchyardDetail';
import { QueueCongestionDetail, type QueueCongestionProperties } from './components/QueueCongestionDetail';
import { TransmissionCongestionDetail, type TransmissionCongestionProperties } from './components/TransmissionCongestionDetail';
import { WaterRegZoneDetail, type WaterRegZoneProperties } from './components/WaterRegZoneDetail';
import { BrackishZoneDetail, type BrackishZoneProperties } from './components/BrackishZoneDetail';
import { TdsSampleDetail, type TdsSampleProperties } from './components/TdsSampleDetail';
import { WaterWellDetail, type WaterWellProperties } from './components/WaterWellDetail';
import { ElectricTerritoryDetail, type ElectricTerritoryProperties } from './components/ElectricTerritoryDetail';
import { ListView } from './components/ListView';
import { CountyReport } from './components/CountyReport';
import { CountyDetail } from './components/CountyDetail';
import { AssembliesReport } from './components/AssembliesReport';
import { AssemblyDetail } from './components/AssemblyDetail';
import { SiteBriefing } from './components/SiteBriefing';
import { PortfolioPage, PineCurtainIntegraPage } from './components/portfolio';
import { CrownPinePage } from './components/portfolio/CrownPinePage';
import { SiteBriefingV2 } from './components/SiteBriefingV2';
import { SiteClusterReport } from './components/cluster/SiteClusterReport';
import { ZoneBriefing } from './components/zone/ZoneBriefing';
import { ZoneSharePage } from './components/zone/ZoneSharePage';
import { fetchZoneList, type ZoneListEntry } from './lib/api';
import AdminPage from './pages/AdminPage';
import DataStatusPage from './pages/DataStatusPage';
import { PipelineInventoryPage } from './pages/PipelineInventoryPage';
import { designTokens } from './styles/design-tokens';
import { fetchDataCenterFreshness, fetchNearestParcel, fetchPipelineOperators, type Filters, type SourceFreshness, type PipelineFilters } from './lib/api';
import { companyLabel, type Bbox, type OperatorCatalogEntry } from './lib/operator-focus';
import { useSavedLocations } from './hooks/useSavedLocations';
type ViewTab = 'map' | 'list' | 'counties' | 'assemblies';

const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const { theme } = useTheme();
  const [filters, setFilters] = useState<Filters>({});
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null);
  const [show3DTerrain, setShow3DTerrain] = useState(false);
  const [selectedZoneSlug, setSelectedZoneSlug] = useState<string | null>(null);
  const [parcelCount, setParcelCount] = useState(0);
  const [pipelineCount, setPipelineCount] = useState(0);
  const [fiberCount, setFiberCount] = useState(0);
  const [showParcels, setShowParcels] = useState(false);
  const [parcelLoading, setParcelLoading] = useState(false);
  const [showFederalLands, setShowFederalLands] = useState(false);
  const [showCrownPineTracts, setShowCrownPineTracts] = useState(false);
  const [showSavedLocations, setShowSavedLocations] = useState(true);
  const savedLocations = useSavedLocations();
  const [pendingSavedFlyTo, setPendingSavedFlyTo] = useState<{ lat: number; lng: number; source_type: string | null; source_id: string | null } | null>(null);
  const [showAssemblyGroups, setShowAssemblyGroups] = useState(false);
  const [showGathering, setShowGathering] = useState(false);
  const [showGasTransmission, setShowGasTransmission] = useState(false);
  const [showLiquidsTransmission, setShowLiquidsTransmission] = useState(false);
  const [showDesertSw, setShowDesertSw] = useState(false);

  const [dcFreshness, setDcFreshness] = useState<SourceFreshness | null>(null);
  const [dcStalenessDismissed, setDcStalenessDismissed] = useState(false);
  const dcFreshnessKey = dcFreshness ? `${dcFreshness.last_reviewed}:${dcFreshness.reviewed_count}:${dcFreshness.total_count}` : '';
  const dcOldestReviewDays = dcFreshness?.oldest_reviewed
    ? Math.floor((Date.now() - new Date(dcFreshness.oldest_reviewed).getTime()) / 86400000) : null;

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchDataCenterFreshness().then(data => {
      setDcFreshness(data);
      setDcStalenessDismissed(localStorage.getItem('pp-dc-stale-dismissed') === `${data.last_reviewed}:${data.reviewed_count}:${data.total_count}`);
    }).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    const sync = () => {
      const m = window.location.hash.match(/^#\/zones\/([^/?]+)/);
      setSelectedZoneSlug(m ? decodeURIComponent(m[1]) : null);
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  const [operators, setOperators] = useState<OperatorInfo[]>([]);
  const [hiddenOperators, setHiddenOperators] = useState<Set<string>>(new Set());
  // Every operator with its owning companies: backs the company picker and focus. Loaded once.
  const [operatorCatalog, setOperatorCatalog] = useState<OperatorCatalogEntry[]>([]);
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchPipelineOperators().then(setOperatorCatalog).catch(err => console.error('Operator catalog failed to load:', err));
  }, [isAuthenticated]);
  const catalogByOperator = useMemo(() => new Map(operatorCatalog.map(e => [e.operator, e])), [operatorCatalog]);
  // Company attribution for the pipeline popup and detail panel (same registry as the focus).
  const companyLabelFor = useCallback((operator: string) => companyLabel(catalogByOperator.get(operator)), [catalogByOperator]);
  const [pendingFitBounds, setPendingFitBounds] = useState<Bbox | null>(() => new URLSearchParams(window.location.search).get('research') === 'memphis' ? MEMPHIS_RESEARCH.siteBounds : null);
  const [etLayers, setEtLayers] = useState<EtLayers>(EMPTY_ET_LAYERS);
  const [etData, setEtData] = useState<EtResearchPayload | null>(null);
  const [etLoading, setEtLoading] = useState(false);
  const [etError, setEtError] = useState<string | null>(null);
  const etRequestRef = useRef<AbortController | null>(null);
  const loadEtResearch = useCallback(() => {
    if (etData || etRequestRef.current || !isAuthenticated) return;
    const controller = new AbortController();
    etRequestRef.current = controller;
    setEtLoading(true);
    setEtError(null);
    fetchEtResearch(controller.signal).then(data => {
      if (!controller.signal.aborted) setEtData(data);
    }).catch(() => {
      if (!controller.signal.aborted) setEtError('ET reference data could not be loaded. Please retry.');
    }).finally(() => {
      if (etRequestRef.current === controller) {
        etRequestRef.current = null;
        setEtLoading(false);
      }
    });
  }, [etData, isAuthenticated]);
  useEffect(() => () => { etRequestRef.current?.abort(); }, []);
  const handleEtLayersChange = useCallback((layers: EtLayers) => {
    setEtLayers(layers);
    if (layers.powergen || layers.intrastate) loadEtResearch();
  }, [loadEtResearch]);
  const handleToggleAllEt = () => handleEtLayersChange(
    etLayers.powergen || etLayers.intrastate ? EMPTY_ET_LAYERS : DEFAULT_ET_LAYERS,
  );
  const [memphisLayers, setMemphisLayers] = useState<MemphisLayers>(() => new URLSearchParams(window.location.search).get('research') === 'memphis' ? DEFAULT_MEMPHIS_LAYERS : EMPTY_MEMPHIS_LAYERS);
  const anyMemphisActive = Object.values(memphisLayers).some(Boolean);
  const handleToggleAllMemphis = () => {
    setMemphisLayers(anyMemphisActive ? EMPTY_MEMPHIS_LAYERS : DEFAULT_MEMPHIS_LAYERS);
    if (!anyMemphisActive) setPendingFitBounds(MEMPHIS_RESEARCH.siteBounds);
  };
  const [showPowerInfra, setShowPowerInfra] = useState(false);
  const [powerLoading, setPowerLoading] = useState(false);
  const [showOsmSwitchyards, setShowOsmSwitchyards] = useState(false);
  const [osmSwitchyardsHifldNewOnly, setOsmSwitchyardsHifldNewOnly] = useState(true);
  const [osmSwitchyardsLoading, setOsmSwitchyardsLoading] = useState(false);
  const [showGridCapacity, setShowGridCapacity] = useState(false);
  const [gridCapacityLoading, setGridCapacityLoading] = useState(false);
  const [gridCapacityTiers, setGridCapacityTiers] = useState<Set<string>>(new Set(['high', 'likely', 'moderate', 'low']));
  const [showQueueHeatmap, setShowQueueHeatmap] = useState(false);
  const [queueHeatmapLoading, setQueueHeatmapLoading] = useState(false);
  const [showCongestionHeatmap, setShowCongestionHeatmap] = useState(false);
  const [congestionHeatmapLoading, setCongestionHeatmapLoading] = useState(false);
  const [showElectricTerritories, setShowElectricTerritories] = useState(false);
  const [showAirportZones, setShowAirportZones] = useState(false);
  const [airportLoading, setAirportLoading] = useState(false);
  const [airportFilter, setAirportFilter] = useState<'all' | 'commercial'>('commercial');
  const [showFiber, setShowFiber] = useState(false);
  const [fiberLoading, setFiberLoading] = useState(false);
  const [showSegra, setShowSegra] = useState(false);
  const [showZayo, setShowZayo] = useState(false);
  const [showFiberLight, setShowFiberLight] = useState(false);
  const [showNtiaMiddleMile, setShowNtiaMiddleMile] = useState(false);
  const [showFiberRowPipelines, setShowFiberRowPipelines] = useState(false);
  const [showIxpFacilities, setShowIxpFacilities] = useState(false);
  const [showWater, setShowWater] = useState(false);
  const [waterLoading, setWaterLoading] = useState(false);
  const [showFloodZones, setShowFloodZones] = useState(false);
  const [floodZoneLoading, setFloodZoneLoading] = useState(false);
  const [showTwdbFloodHazards, setShowTwdbFloodHazards] = useState(false);
  const [twdbFloodHazardsLoading, setTwdbFloodHazardsLoading] = useState(false);
  const [showHighways, setShowHighways] = useState(false);
  const [highwayLoading, setHighwayLoading] = useState(false);
  const [showRoads, setShowRoads] = useState(false);
  const [roadsLoading, setRoadsLoading] = useState(false);
  // OSM pipeline candidates: a non-scoring research overlay, OFF by default (ODbL attribution shown when on).
  const [showOsmPipelineCandidates, setShowOsmPipelineCandidates] = useState(false);
  const [osmPipelineCandidatesLoading, setOsmPipelineCandidatesLoading] = useState(false);
  // Georgia research site outline (static county polygon), OFF by default like the candidates.
  const [showGeorgiaSites, setShowGeorgiaSites] = useState(false);
  const [showGeorgiaEvidence, setShowGeorgiaEvidence] = useState(false);
  const [showRailroads, setShowRailroads] = useState(false);
  const [railroadLoading, setRailroadLoading] = useState(false);
  const [showDataCenters, setShowDataCenters] = useState(false);
  const [dcLoading, setDcLoading] = useState(false);
  const [showStateTrustLands, setShowStateTrustLands] = useState(false);
  const [trustLandLoading, setTrustLandLoading] = useState(false);
  const [showProtectedAreas, setShowProtectedAreas] = useState(false);
  const [showOperatorAois, setShowOperatorAois] = useState(false);
  const [showLandListings, setShowLandListings] = useState(false);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [showGridListings, setShowGridListings] = useState(false);
  const [gridListingsLoading, setGridListingsLoading] = useState(false);
  const [showGasPlants, setShowGasPlants] = useState(false);
  const [gasPlantLoading, setGasPlantLoading] = useState(false);
  const [showCompressors, setShowCompressors] = useState(false);
  const [showReceiptDelivery, setShowReceiptDelivery] = useState(false);
  const [showInterconnects, setShowInterconnects] = useState(false);
  const [showGasStorage, setShowGasStorage] = useState(false);
  const [gasInfraLoading, setGasInfraLoading] = useState(false);
  const [showEpaZones, setShowEpaZones] = useState(false);
  const [epaZoneLoading, setEpaZoneLoading] = useState(false);
  const [showEjScreen, setShowEjScreen] = useState(false);
  const [ejScreenLoading, setEjScreenLoading] = useState(false);
  const [showCriticalHabitat, setShowCriticalHabitat] = useState(false);
  const [showWetlands, setShowWetlands] = useState(false);
  const [showSeismicRisk, setShowSeismicRisk] = useState(false);
  const [showUrbanAreas, setShowUrbanAreas] = useState(false);
  const [showWildfire, setShowWildfire] = useState(false);
  const [wildfireLoading, setWildfireLoading] = useState(false);
  const [showMineSubsidence, setShowMineSubsidence] = useState(false);
  const [showAmlSites, setShowAmlSites] = useState(false);
  const [showWaterRegulatoryZones, setShowWaterRegulatoryZones] = useState(false);
  const [showTwdbWells, setShowTwdbWells] = useState(false);
  const [showBrackish, setShowBrackish] = useState(false);
  const [showTdsSamples, setShowTdsSamples] = useState(false);
  const [showGasWells, setShowGasWells] = useState(false);
  const [showOilWells, setShowOilWells] = useState(false);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [wellCount, setWellCount] = useState(0);
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineProperties | null>(null);
  const [selectedCarrierFiber, setSelectedCarrierFiber] = useState<string | null>(null);
  const [selectedRailroad, setSelectedRailroad] = useState<import('./components/RailroadDetail').RailroadProperties | null>(null);
  const [selectedIxpFacility, setSelectedIxpFacility] = useState<IxpFacilityProperties | null>(null);
  const [selectedWell, setSelectedWell] = useState<{ id: number; flaringMcfDay?: number } | null>(null);
  const [selectedDataCenter, setSelectedDataCenter] = useState<DataCenterProperties | null>(null);
  const [selectedListing, setSelectedListing] = useState<LandListingProperties | null>(null);
  const [selectedFloodZone, setSelectedFloodZone] = useState<FloodZoneProperties | null>(null);
  const [selectedMineSubsidence, setSelectedMineSubsidence] = useState<MineSubsidenceProperties | null>(null);
  const [selectedAmlSite, setSelectedAmlSite] = useState<AmlSiteProperties | null>(null);
  const [selectedWildfireCounty, setSelectedWildfireCounty] = useState<WildfireProperties | null>(null);
  const [selectedUrbanArea, setSelectedUrbanArea] = useState<UrbanAreaProperties | null>(null);
  const [selectedSeismicPoint, setSelectedSeismicPoint] = useState<SeismicRiskProperties | null>(null);
  const [selectedGasInfra, setSelectedGasInfra] = useState<GasInfraProperties | null>(null);
  const [selectedSubstation, setSelectedSubstation] = useState<SubstationProperties | null>(null);
  const [selectedAirport, setSelectedAirport] = useState<AirportProperties | null>(null);
  const [selectedOsmSwitchyard, setSelectedOsmSwitchyard] = useState<OsmSwitchyardProperties | null>(null);
  const [selectedQueueCounty, setSelectedQueueCounty] = useState<QueueCongestionProperties | null>(null);
  const [selectedCongestionCounty, setSelectedCongestionCounty] = useState<TransmissionCongestionProperties | null>(null);
  const [selectedElectricTerritory, setSelectedElectricTerritory] = useState<ElectricTerritoryProperties | null>(null);
  const [selectedWaterRegZone, setSelectedWaterRegZone] = useState<WaterRegZoneProperties | null>(null);
  const [selectedBrackishZone, setSelectedBrackishZone] = useState<BrackishZoneProperties | null>(null);
  const [selectedTdsSample, setSelectedTdsSample] = useState<TdsSampleProperties | null>(null);
  const [selectedWaterWell, setSelectedWaterWell] = useState<WaterWellProperties | null>(null);
  const [pendingFlyTo, setPendingFlyTo] = useState<[number, number] | null>(null);
  const [pipelineFilters, setPipelineFilters] = useState<PipelineFilters>({});
  const [measurementActive, setMeasurementActive] = useState(false);
  const [areaActive, setAreaActive] = useState(false);
  // The two measure tools are mutually exclusive — arming one disarms the other.
  const armMeasurement = useCallback((v: boolean) => { setMeasurementActive(v); if (v) setAreaActive(false); }, []);
  const armArea = useCallback((v: boolean) => { setAreaActive(v); if (v) setMeasurementActive(false); }, []);
  const [isSatellite, setIsSatellite] = useState(false);
  const [showCountyLines, setShowCountyLines] = useState(false);
  const [showSlopeDrape, setShowSlopeDrape] = useState(false);
  const [showSlope1m, setShowSlope1m] = useState(false);
  const [slopeDrapeOpacity, setSlopeDrapeOpacity] = useState(0.55);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [activeTab, setActiveTab] = useState<ViewTab>('map');

  const [cursorCoords, setCursorCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapZoom, setMapZoom] = useState(8);
  const [selectedParcelCounty, setSelectedParcelCounty] = useState<string | null>(null);
  const [selectedCountyDetail, setSelectedCountyDetail] = useState<{ state: string; county: string; rank?: number; composite?: number } | null>(null);
  const [selectedAssemblyId, setSelectedAssemblyId] = useState<string | null>(null);
  const [assemblyMapMode, setAssemblyMapMode] = useState(false);
  const [pendingCountyZoom, setPendingCountyZoom] = useState<[number, number, number, number] | null>(null);
  const [searchPin, setSearchPin] = useState<[number, number] | null>(null);

  const handleSelectParcel = useCallback((id: string | null) => {
    setSelectedParcelId(id);
    if (id) { setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); }
  }, []);

  const handleSelectCarrierFiber = useCallback((carrier: string | null) => {
    setSelectedCarrierFiber(carrier);
    if (carrier) {
      setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null);
      setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null);
      setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null);
      setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedIxpFacility(null);
      setSelectedRailroad(null);
    }
  }, []);

  const handleSelectRailroad = useCallback((rr: import('./components/RailroadDetail').RailroadProperties | null) => {
    setSelectedRailroad(rr);
    if (rr) {
      setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null);
      setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null);
      setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null);
      setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedIxpFacility(null);
      setSelectedCarrierFiber(null);
    }
  }, []);

  const handleSelectIxpFacility = useCallback((fac: IxpFacilityProperties | null) => {
    setSelectedIxpFacility(fac);
    if (fac) {
      setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null);
      setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null);
      setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null);
      setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null);
    }
  }, []);

  const handleSelectPipeline = useCallback((pipeline: PipelineProperties | null) => {
    setSelectedPipeline(pipeline);
    if (pipeline) { setSelectedParcelId(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectWell = useCallback((wellId: number | null, flaringMcfDay?: number) => {
    setSelectedWell(wellId != null ? { id: wellId, flaringMcfDay } : null);
    if (wellId != null) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectDataCenter = useCallback((dc: DataCenterProperties | null) => {
    setSelectedDataCenter(dc);
    if (dc) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectListing = useCallback((listing: LandListingProperties | null) => {
    setSelectedListing(listing);
    if (listing) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectFloodZone = useCallback((fz: FloodZoneProperties | null) => {
    setSelectedFloodZone(fz);
    if (fz) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedMineSubsidence(null); setSelectedAmlSite(null); setSelectedWildfireCounty(null); setSelectedUrbanArea(null); setSelectedSeismicPoint(null); }
  }, []);

  const clearOtherDetailsForEnv = useCallback(() => {
    setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null);
    setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null);
    setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null);
    setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null);
    setSelectedWaterWell(null);
  }, []);

  const handleSelectMineSubsidence = useCallback((z: MineSubsidenceProperties | null) => {
    setSelectedMineSubsidence(z);
    if (z) { clearOtherDetailsForEnv(); setSelectedAmlSite(null); setSelectedWildfireCounty(null); setSelectedUrbanArea(null); setSelectedSeismicPoint(null); }
  }, [clearOtherDetailsForEnv]);

  const handleSelectAmlSite = useCallback((s: AmlSiteProperties | null) => {
    setSelectedAmlSite(s);
    if (s) { clearOtherDetailsForEnv(); setSelectedMineSubsidence(null); setSelectedWildfireCounty(null); setSelectedUrbanArea(null); setSelectedSeismicPoint(null); }
  }, [clearOtherDetailsForEnv]);

  const handleSelectWildfireCounty = useCallback((c: WildfireProperties | null) => {
    setSelectedWildfireCounty(c);
    if (c) { clearOtherDetailsForEnv(); setSelectedMineSubsidence(null); setSelectedAmlSite(null); setSelectedUrbanArea(null); setSelectedSeismicPoint(null); }
  }, [clearOtherDetailsForEnv]);

  const handleSelectUrbanArea = useCallback((a: UrbanAreaProperties | null) => {
    setSelectedUrbanArea(a);
    if (a) { clearOtherDetailsForEnv(); setSelectedMineSubsidence(null); setSelectedAmlSite(null); setSelectedWildfireCounty(null); setSelectedSeismicPoint(null); }
  }, [clearOtherDetailsForEnv]);

  const handleSelectSeismicPoint = useCallback((p: SeismicRiskProperties | null) => {
    setSelectedSeismicPoint(p);
    if (p) { clearOtherDetailsForEnv(); setSelectedMineSubsidence(null); setSelectedAmlSite(null); setSelectedWildfireCounty(null); setSelectedUrbanArea(null); }
  }, [clearOtherDetailsForEnv]);

  const handleSelectGasInfra = useCallback((gi: GasInfraProperties | null) => {
    setSelectedGasInfra(gi);
    if (gi) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedWaterRegZone(null); setSelectedSubstation(null); }
  }, []);

  const handleSelectSubstation = useCallback((sub: SubstationProperties | null) => {
    setSelectedSubstation(sub);
    if (sub) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); setSelectedOsmSwitchyard(null); setSelectedAirport(null); }
  }, []);

  const handleSelectAirport = useCallback((ap: AirportProperties | null) => {
    setSelectedAirport(ap);
    if (ap) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedSubstation(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); setSelectedOsmSwitchyard(null); }
  }, []);

  const handleSelectOsmSwitchyard = useCallback((sw: OsmSwitchyardProperties | null) => {
    setSelectedOsmSwitchyard(sw);
    if (sw) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedSubstation(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); }
  }, []);

  const handleSelectQueueCounty = useCallback((qc: QueueCongestionProperties | null) => {
    setSelectedQueueCounty(qc);
    if (qc) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedSubstation(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); setSelectedCongestionCounty(null); }
  }, []);

  const handleSelectCongestionCounty = useCallback((cc: TransmissionCongestionProperties | null) => {
    setSelectedCongestionCounty(cc);
    if (cc) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedSubstation(null); setSelectedQueueCounty(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); }
  }, []);

  const handleSelectElectricTerritory = useCallback((t: ElectricTerritoryProperties | null) => {
    setSelectedElectricTerritory(t);
    if (t) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedSubstation(null); setSelectedQueueCounty(null); setSelectedCongestionCounty(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); setSelectedCarrierFiber(null); }
  }, []);

  const handleSelectWaterRegZone = useCallback((zone: WaterRegZoneProperties | null) => {
    setSelectedWaterRegZone(zone);
    if (zone) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectBrackishZone = useCallback((zone: BrackishZoneProperties | null) => {
    setSelectedBrackishZone(zone);
    if (zone) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedTdsSample(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectTdsSample = useCallback((sample: TdsSampleProperties | null) => {
    setSelectedTdsSample(sample);
    if (sample) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedWaterWell(null); }
  }, []);

  const handleSelectWaterWell = useCallback((well: WaterWellProperties | null) => {
    setSelectedWaterWell(well);
    if (well) { setSelectedParcelId(null); setSelectedPipeline(null); setSelectedWell(null); setSelectedCarrierFiber(null); setSelectedIxpFacility(null); setSelectedDataCenter(null); setSelectedListing(null); setSelectedFloodZone(null); setSelectedGasInfra(null); setSelectedWaterRegZone(null); setSelectedBrackishZone(null); setSelectedTdsSample(null); }
  }, []);

  const toggleOperator = useCallback((name: string) => {
    setHiddenOperators(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);
  // Parent rows hide or show every entity under them in one step.
  const setOperatorsHidden = useCallback((names: string[], hidden: boolean) => {
    setHiddenOperators(prev => {
      const next = new Set(prev);
      names.forEach(n => (hidden ? next.add(n) : next.delete(n)));
      return next;
    });
  }, []);
  const showAllOperators = useCallback(() => setHiddenOperators(new Set()), []);

  // Stable sorted array for MapView dependency (Set causes infinite re-renders)
  const hiddenOperatorsArray = useMemo(
    () => Array.from(hiddenOperators).sort(),
    [hiddenOperators]
  );

  const anyPipelineActive = showGasTransmission || showGathering || showLiquidsTransmission || showDesertSw;

  const handleToggleAllPipelines = useCallback(() => {
    if (anyPipelineActive) {
      setShowGasTransmission(false);
      setShowGathering(false);
      setShowLiquidsTransmission(false);
      setShowDesertSw(false);
    } else {
      setShowGasTransmission(true);
    }
  }, [anyPipelineActive]);

  // Georgia section: the Dalton site outline and the OSM candidate corridors behind one header
  // toggle. Turning them on also fits the map to the site and its three nearest gas corridors.
  const anyGeorgiaActive = showGeorgiaSites || showOsmPipelineCandidates || showGeorgiaEvidence;
  const handleToggleAllGeorgia = useCallback(() => {
    const target = !anyGeorgiaActive;
    setShowGeorgiaSites(target); setShowOsmPipelineCandidates(target); setShowGeorgiaEvidence(target);
    if (target) setPendingFitBounds(GEORGIA_SITE_BOUNDS);
  }, [anyGeorgiaActive]);
  const handleZoomToGeorgiaSite = useCallback(() => setPendingFitBounds(GEORGIA_SITE_BOUNDS), []);

  const anyCarrierActive = showSegra || showZayo || showFiberLight || showNtiaMiddleMile;
  const anyFiberActive = showFiber || anyCarrierActive || showFiberRowPipelines || showIxpFacilities;
  const handleToggleAllFiber = useCallback(() => {
    const target = !anyFiberActive;
    setShowFiber(target); setShowSegra(target); setShowZayo(target); setShowFiberLight(target); setShowNtiaMiddleMile(target); setShowFiberRowPipelines(target); setShowIxpFacilities(target);
  }, [anyFiberActive]);

  const anyInfraActive = showAirportZones || showHighways || showRoads || showRailroads || showPowerInfra || showGridCapacity || showQueueHeatmap || showCongestionHeatmap || showElectricTerritories;
  const handleToggleAllInfra = useCallback(() => {
    const target = !anyInfraActive;
    setShowAirportZones(target); setShowHighways(target); setShowRoads(target);
    setShowRailroads(target); setShowPowerInfra(target); setShowGridCapacity(target); setShowQueueHeatmap(target); setShowElectricTerritories(target);
  }, [anyInfraActive]);

  const anyWaterActive = showWater || showTwdbWells || showBrackish || showTdsSamples || showWaterRegulatoryZones;
  const handleToggleAllWater = useCallback(() => {
    const target = !anyWaterActive;
    setShowWater(target); setShowTwdbWells(target); setShowBrackish(target); setShowTdsSamples(target); setShowWaterRegulatoryZones(target);
  }, [anyWaterActive]);

  const anyEnvActive = showEpaZones || showEjScreen || showFloodZones || showTwdbFloodHazards || showProtectedAreas || showCriticalHabitat || showWetlands || showSeismicRisk || showUrbanAreas || showWildfire || showMineSubsidence || showAmlSites;
  const handleToggleAllEnv = useCallback(() => {
    const target = !anyEnvActive;
    setShowEpaZones(target); setShowEjScreen(target); setShowFloodZones(target); setShowTwdbFloodHazards(target);
    setShowProtectedAreas(target); setShowCriticalHabitat(target); setShowWetlands(target);
    setShowSeismicRisk(target); setShowUrbanAreas(target); setShowWildfire(target);
    setShowMineSubsidence(target); setShowAmlSites(target);
  }, [anyEnvActive]);

  const anyLandActive = showDataCenters || showFederalLands || showCrownPineTracts || showAssemblyGroups || showStateTrustLands || showLandListings || showSlopeDrape || showSlope1m;
  const handleToggleAllLand = useCallback(() => {
    const target = !anyLandActive;
    setShowDataCenters(target); setShowFederalLands(target); setShowCrownPineTracts(target); setShowAssemblyGroups(target);
    setShowStateTrustLands(target); setShowLandListings(target); setShowSlopeDrape(target); setShowSlope1m(target);
  }, [anyLandActive]);

  const handleResetAllLayers = useCallback(() => {
    setEtLayers(EMPTY_ET_LAYERS);
    setShowGasWells(false); setShowOilWells(false);
    setShowAirportZones(false); setShowFiber(false); setShowHighways(false); setShowRoads(false); setShowOsmPipelineCandidates(false); setShowGeorgiaSites(false); setShowGeorgiaEvidence(false);
    setShowRailroads(false); setShowPowerInfra(false); setShowGridCapacity(false); setShowQueueHeatmap(false);
    setShowGasPlants(false); setShowCompressors(false); setShowReceiptDelivery(false);
    setShowInterconnects(false); setShowGasStorage(false); setShowOperatorAois(false);
    setShowWater(false); setShowTwdbWells(false); setShowBrackish(false); setShowTdsSamples(false); setShowWaterRegulatoryZones(false);
    setShowEpaZones(false); setShowEjScreen(false); setShowFloodZones(false); setShowTwdbFloodHazards(false);
    setShowProtectedAreas(false); setShowCriticalHabitat(false); setShowWetlands(false);
    setShowSeismicRisk(false); setShowUrbanAreas(false); setShowWildfire(false);
    setShowMineSubsidence(false); setShowAmlSites(false);
    setShowDataCenters(false); setShowFederalLands(false); setShowCrownPineTracts(false); setShowAssemblyGroups(false);
    setShowStateTrustLands(false); setShowLandListings(false); setShowSlopeDrape(false); setShowSlope1m(false);
    setShowSegra(false); setShowZayo(false); setShowFiberLight(false); setShowNtiaMiddleMile(false); setShowFiberRowPipelines(false); setShowIxpFacilities(false);
  }, []);

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await login(password);
    if (!result.success) setLoginError(result.error || 'Failed');
  }, [login, password]);


  const handleCountyViewOnMap = useCallback((bbox: [number, number, number, number]) => {
    setPendingCountyZoom(bbox);
    setSelectedCountyDetail(null);
    setActiveTab('map');
  }, []);

  const handleSiteSearch = useCallback(async (query: string): Promise<string | null> => {
    const q = query.trim();
    if (!q) return 'Enter coordinates';
    setSearchPin(null);

    // Coordinate pattern: "31.4, -103.2" or "31.4 -103.2"
    const coordMatch = q.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
    if (!coordMatch) return 'Try: 31.4, -103.2';

    const a = parseFloat(coordMatch[1]);
    const b = parseFloat(coordMatch[2]);
    let lat: number, lng: number;
    if (a >= -90 && a <= 90 && b >= -180 && b <= 180 && b < 0) {
      lat = a; lng = b;
    } else if (b >= -90 && b <= 90 && a >= -180 && a <= 180 && a < 0) {
      lat = b; lng = a;
    } else {
      lat = a; lng = b;
    }

    setPendingFlyTo([lng, lat]);
    setSearchPin([lng, lat]);
    setActiveTab('map');
    try {
      const result = await fetchNearestParcel(lat, lng);
      handleSelectParcel(result.id);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) console.error('[Search] Nearest parcel error:', err);
    }
    return null;
  }, [handleSelectParcel, setPendingFlyTo, setActiveTab]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: designTokens.colors.background,
        fontFamily: designTokens.typography.fontFamily.display,
      }}>
        <form onSubmit={handleLogin} style={{
          width: '320px', textAlign: 'center',
        }}>
          <img
            src="/atwater-logo-light.svg"
            alt="Atwater Energy"
            style={{ width: '260px', marginBottom: '32px' }}
          />
          <input
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); setLoginError(''); }}
            placeholder="Password"
            autoFocus
            style={{
              width: '100%', padding: '11px 14px',
              border: `1px solid ${passwordFocused ? 'rgba(196,112,90,0.6)' : designTokens.colors.borderLight}`,
              borderRadius: '6px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box',
              fontFamily: designTokens.typography.fontFamily.display,
              background: designTokens.colors.backgroundSecondary, color: designTokens.colors.text,
              outline: 'none',
            }}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
          />
          {loginError && (
            <p style={{ color: designTokens.colors.error, fontSize: '12px', marginBottom: '8px' }}>{loginError}</p>
          )}
          <button type="submit" style={{
            width: '100%', padding: '11px', background: designTokens.colors.brand,
            color: '#E4E6E8', border: 'none', borderRadius: '6px', fontSize: '14px',
            fontWeight: 500, cursor: 'pointer', fontFamily: designTokens.typography.fontFamily.display,
            letterSpacing: '0.04em',
          }}>
            Sign In
          </button>
        </form>
      </div>
    );
  }


  // County detail (full page takeover, like SitePackage)
  if (selectedCountyDetail) {
    return (
      <CountyDetail
        state={selectedCountyDetail.state}
        county={selectedCountyDetail.county}
        rank={selectedCountyDetail.rank}
        compositeScore={selectedCountyDetail.composite}
        onClose={() => setSelectedCountyDetail(null)}
        onViewOnMap={handleCountyViewOnMap}
      />
    );
  }

  // Counties view (full page replacement)
  if (activeTab === 'counties') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: designTokens.typography.fontFamily.display, background: theme.background, color: theme.text }}>
        <Toolbar activeTab={activeTab} onTabChange={setActiveTab} activeZoneSlug={selectedZoneSlug} cursorCoords={null} mapZoom={null} onSearch={handleSiteSearch} />
        <CountyReport onSelectCounty={(s, c, rank, composite) => setSelectedCountyDetail({ state: s, county: c, rank, composite })} />
      </div>
    );
  }

  // Assembly detail (full page takeover — skip when viewing a parcel on the map)
  if (selectedAssemblyId && !assemblyMapMode) {
    return (
      <AssemblyDetail
        assemblyId={selectedAssemblyId}
        onClose={() => { setSelectedAssemblyId(null); setAssemblyMapMode(false); }}
        onViewOnMap={(lat, lng, tight) => {
          setAssemblyMapMode(true);
          setActiveTab('map');
          const d = tight ? 0.02 : 0.15;
          setPendingCountyZoom([lng - d, lat - d, lng + d, lat + d]);
        }}
      />
    );
  }

  // Assemblies view (full page replacement)
  if (activeTab === 'assemblies') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: designTokens.typography.fontFamily.display, background: '#E4E6EA', color: '#2E3840' }}>
        <Toolbar activeTab={activeTab} onTabChange={setActiveTab} activeZoneSlug={selectedZoneSlug} cursorCoords={null} mapZoom={null} onSearch={handleSiteSearch} />
        <AssembliesReport onSelectAssembly={(id) => { setSelectedAssemblyId(id); setAssemblyMapMode(false); }} />
      </div>
    );
  }

  // Main app layout
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden',
      fontFamily: designTokens.typography.fontFamily.display, background: theme.background, color: theme.text,
    }}>
      {/* Top toolbar */}
      <Toolbar activeTab={activeTab} onTabChange={setActiveTab} activeZoneSlug={selectedZoneSlug} cursorCoords={cursorCoords} mapZoom={mapZoom} onSearch={handleSiteSearch} />

      {/* Data center staleness banner */}
      {dcFreshness && (dcFreshness.reviewed_count < dcFreshness.total_count || dcOldestReviewDays === null || dcOldestReviewDays >= 30) && !dcStalenessDismissed && (
        <div className="app-staleness-banner" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '6px 16px', fontSize: designTokens.typography.fontSize.sm,
          background: designTokens.colors.warningLight, color: designTokens.colors.warning,
          borderBottom: `1px solid ${designTokens.colors.warning}33`,
        }}>
          <span>Data-center evidence reviewed for {dcFreshness.reviewed_count} of {dcFreshness.total_count} curated records.
            {dcFreshness.last_reviewed ? ` Latest review: ${dcFreshness.last_reviewed.slice(0, 10)}.` : ' Source review dates are not yet recorded.'}
            {dcOldestReviewDays != null && dcOldestReviewDays >= 30 ? ` Oldest recorded review: ${dcOldestReviewDays} days ago.` : ''}
          </span>
          <button onClick={() => {
            setDcStalenessDismissed(true);
            localStorage.setItem('pp-dc-stale-dismissed', dcFreshnessKey);
          }} style={{
            background: 'none', border: 'none', color: designTokens.colors.warning,
            cursor: 'pointer', padding: '0 4px', fontSize: 14, lineHeight: 1, opacity: 0.7,
          }}>x</button>
        </div>
      )}

      {/* Main row: sidebar + center + detail */}
      <div className="app-main-content" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left sidebar: filters */}
        <FilterSidebar
          filters={filters}
          onChange={setFilters}
          pipelineFilters={pipelineFilters}
          onPipelineFiltersChange={setPipelineFilters}
          showParcels={showParcels}
          onToggleParcels={() => setShowParcels(!showParcels)}
          parcelLoading={parcelLoading}
          showFederalLands={showFederalLands}
          onToggleFederalLands={() => setShowFederalLands(!showFederalLands)}
          showCrownPineTracts={showCrownPineTracts}
          onToggleCrownPineTracts={() => setShowCrownPineTracts(!showCrownPineTracts)}
          showSavedLocations={showSavedLocations}
          onToggleSavedLocations={() => setShowSavedLocations(!showSavedLocations)}
          savedLocations={savedLocations.locations}
          savedLocationsLoading={savedLocations.loading}
          onClickSavedLocation={(loc: { lat: number; lng: number; source_type: string | null; source_id: string | null }) => setPendingSavedFlyTo({ lat: loc.lat, lng: loc.lng, source_type: loc.source_type, source_id: loc.source_id })}
          onRenameSavedLocation={savedLocations.rename}
          onDeleteSavedLocation={savedLocations.remove}
          showAssemblyGroups={showAssemblyGroups}
          onToggleAssemblyGroups={() => setShowAssemblyGroups(!showAssemblyGroups)}
          showGathering={showGathering}
          onToggleGathering={() => setShowGathering(!showGathering)}
          showGasTransmission={showGasTransmission}
          onToggleGasTransmission={() => setShowGasTransmission(!showGasTransmission)}
          showLiquidsTransmission={showLiquidsTransmission}
          onToggleLiquidsTransmission={() => setShowLiquidsTransmission(!showLiquidsTransmission)}
          showDesertSw={showDesertSw}
          onToggleDesertSw={() => setShowDesertSw(!showDesertSw)}
          anyPipelineActive={anyPipelineActive}
          onToggleAllPipelines={handleToggleAllPipelines}
          operators={operators}
          hiddenOperators={hiddenOperators}
          onToggleOperator={toggleOperator}
          onSetOperatorsHidden={setOperatorsHidden}
          onShowAllOperators={showAllOperators}
          operatorCatalog={operatorCatalog}
          onFitBounds={setPendingFitBounds}
          showPowerInfra={showPowerInfra}
          onTogglePowerInfra={() => setShowPowerInfra(!showPowerInfra)}
          powerLoading={powerLoading}
          showOsmSwitchyards={showOsmSwitchyards}
          onToggleOsmSwitchyards={() => setShowOsmSwitchyards(!showOsmSwitchyards)}
          osmSwitchyardsHifldNewOnly={osmSwitchyardsHifldNewOnly}
          onToggleOsmSwitchyardsHifldNewOnly={() => setOsmSwitchyardsHifldNewOnly(!osmSwitchyardsHifldNewOnly)}
          osmSwitchyardsLoading={osmSwitchyardsLoading}
          showGridCapacity={showGridCapacity}
          onToggleGridCapacity={() => setShowGridCapacity(!showGridCapacity)}
          gridCapacityLoading={gridCapacityLoading}
          gridCapacityTiers={gridCapacityTiers}
          onToggleGridCapacityTier={(tier: string) => setGridCapacityTiers(prev => {
            const next = new Set(prev);
            if (next.has(tier)) next.delete(tier); else next.add(tier);
            return next;
          })}
          showQueueHeatmap={showQueueHeatmap}
          onToggleQueueHeatmap={() => setShowQueueHeatmap(!showQueueHeatmap)}
          queueHeatmapLoading={queueHeatmapLoading}
          showCongestionHeatmap={showCongestionHeatmap}
          onToggleCongestionHeatmap={() => setShowCongestionHeatmap(!showCongestionHeatmap)}
          congestionHeatmapLoading={congestionHeatmapLoading}
          showElectricTerritories={showElectricTerritories}
          onToggleElectricTerritories={() => setShowElectricTerritories(!showElectricTerritories)}
          showAirportZones={showAirportZones}
          onToggleAirportZones={() => setShowAirportZones(!showAirportZones)}
          airportLoading={airportLoading}
          airportFilter={airportFilter}
          onAirportFilterChange={setAirportFilter}
          showFiber={showFiber}
          onToggleFiber={() => setShowFiber(!showFiber)}
          fiberLoading={fiberLoading}
          showSegra={showSegra}
          onToggleSegra={() => setShowSegra(!showSegra)}
          showZayo={showZayo}
          onToggleZayo={() => setShowZayo(!showZayo)}
          showFiberLight={showFiberLight}
          onToggleFiberLight={() => setShowFiberLight(!showFiberLight)}
          showNtiaMiddleMile={showNtiaMiddleMile}
          onToggleNtiaMiddleMile={() => setShowNtiaMiddleMile(!showNtiaMiddleMile)}
          showFiberRowPipelines={showFiberRowPipelines}
          onToggleFiberRowPipelines={() => setShowFiberRowPipelines(!showFiberRowPipelines)}
          showIxpFacilities={showIxpFacilities}
          onToggleIxpFacilities={() => setShowIxpFacilities(!showIxpFacilities)}
          showWater={showWater}
          onToggleWater={() => setShowWater(!showWater)}
          waterLoading={waterLoading}
          showDataCenters={showDataCenters}
          onToggleDataCenters={() => setShowDataCenters(!showDataCenters)}
          dcLoading={dcLoading}
          showFloodZones={showFloodZones}
          onToggleFloodZones={() => setShowFloodZones(!showFloodZones)}
          floodZoneLoading={floodZoneLoading}
          showTwdbFloodHazards={showTwdbFloodHazards}
          onToggleTwdbFloodHazards={() => setShowTwdbFloodHazards(!showTwdbFloodHazards)}
          twdbFloodHazardsLoading={twdbFloodHazardsLoading}
          showHighways={showHighways}
          onToggleHighways={() => setShowHighways(!showHighways)}
          highwayLoading={highwayLoading}
          showRoads={showRoads}
          onToggleRoads={() => setShowRoads(!showRoads)}
          roadsLoading={roadsLoading}
          showOsmPipelineCandidates={showOsmPipelineCandidates}
          onToggleOsmPipelineCandidates={() => setShowOsmPipelineCandidates(!showOsmPipelineCandidates)}
          osmPipelineCandidatesLoading={osmPipelineCandidatesLoading}
          memphisLayers={memphisLayers}
          etLayers={etLayers}
          etData={etData}
          etLoading={etLoading}
          etError={etError}
          onEtLoad={loadEtResearch}
          onEtLayersChange={handleEtLayersChange}
          onEtZoom={setPendingFitBounds}
          onToggleAllEt={handleToggleAllEt}
          onMemphisLayersChange={setMemphisLayers}
          onMemphisZoom={setPendingFitBounds}
          onToggleAllMemphis={handleToggleAllMemphis}
          showGeorgiaSites={showGeorgiaSites}
          onToggleGeorgiaSites={() => setShowGeorgiaSites(!showGeorgiaSites)}
          showGeorgiaEvidence={showGeorgiaEvidence}
          onToggleGeorgiaEvidence={() => setShowGeorgiaEvidence(!showGeorgiaEvidence)}
          anyGeorgiaActive={anyGeorgiaActive}
          onToggleAllGeorgia={handleToggleAllGeorgia}
          onZoomToGeorgiaSite={handleZoomToGeorgiaSite}
          showRailroads={showRailroads}
          onToggleRailroads={() => setShowRailroads(!showRailroads)}
          railroadLoading={railroadLoading}
          showGasWells={showGasWells}
          onToggleGasWells={() => setShowGasWells(!showGasWells)}
          showOilWells={showOilWells}
          onToggleOilWells={() => setShowOilWells(!showOilWells)}
          wellsLoading={wellsLoading}
          showStateTrustLands={showStateTrustLands}
          onToggleStateTrustLands={() => setShowStateTrustLands(!showStateTrustLands)}
          trustLandLoading={trustLandLoading}
          showProtectedAreas={showProtectedAreas}
          onToggleProtectedAreas={() => setShowProtectedAreas(!showProtectedAreas)}
          showOperatorAois={showOperatorAois}
          onToggleOperatorAois={() => setShowOperatorAois(!showOperatorAois)}
          showLandListings={showLandListings}
          onToggleLandListings={() => setShowLandListings(!showLandListings)}
          listingsLoading={listingsLoading}
          showGridListings={showGridListings}
          onToggleGridListings={() => setShowGridListings(!showGridListings)}
          gridListingsLoading={gridListingsLoading}
          showGasPlants={showGasPlants}
          onToggleGasPlants={() => setShowGasPlants(!showGasPlants)}
          gasPlantLoading={gasPlantLoading}
          showCompressors={showCompressors}
          onToggleCompressors={() => setShowCompressors(!showCompressors)}
          showReceiptDelivery={showReceiptDelivery}
          onToggleReceiptDelivery={() => setShowReceiptDelivery(!showReceiptDelivery)}
          showInterconnects={showInterconnects}
          onToggleInterconnects={() => setShowInterconnects(!showInterconnects)}
          showGasStorage={showGasStorage}
          onToggleGasStorage={() => setShowGasStorage(!showGasStorage)}
          gasInfraLoading={gasInfraLoading}
          anyGasSupplyActive={showGasPlants || showCompressors || showReceiptDelivery || showInterconnects || showGasStorage || showOperatorAois}
          onToggleAllGasSupply={() => {
            const anyOn = showGasPlants || showCompressors || showReceiptDelivery || showInterconnects || showGasStorage || showOperatorAois;
            setShowGasPlants(!anyOn); setShowCompressors(!anyOn); setShowReceiptDelivery(!anyOn); setShowInterconnects(!anyOn); setShowGasStorage(!anyOn); setShowOperatorAois(!anyOn);
          }}
          anyFiberActive={anyFiberActive}
          onToggleAllFiber={handleToggleAllFiber}
          anyInfraActive={anyInfraActive}
          onToggleAllInfra={handleToggleAllInfra}
          anyWaterActive={anyWaterActive}
          onToggleAllWater={handleToggleAllWater}
          showTwdbWells={showTwdbWells}
          onToggleTwdbWells={() => setShowTwdbWells(!showTwdbWells)}
          showBrackish={showBrackish}
          onToggleBrackish={() => setShowBrackish(!showBrackish)}
          showTdsSamples={showTdsSamples}
          onToggleTdsSamples={() => setShowTdsSamples(!showTdsSamples)}
          showWaterRegulatoryZones={showWaterRegulatoryZones}
          onToggleWaterRegulatoryZones={() => setShowWaterRegulatoryZones(!showWaterRegulatoryZones)}
          anyEnvActive={anyEnvActive}
          onToggleAllEnv={handleToggleAllEnv}
          anyLandActive={anyLandActive}
          onToggleAllLand={handleToggleAllLand}
          activeLayerCount={[
            etLayers.powergen, etLayers.intrastate,
            showGasWells, showOilWells,
            showGasPlants, showCompressors, showReceiptDelivery, showInterconnects, showGasStorage, showOperatorAois,
            showAirportZones, showFiber, showHighways, showRoads, showRailroads, showPowerInfra, showGridCapacity, showQueueHeatmap,
            showWater, showTwdbWells, showWaterRegulatoryZones,
            showEpaZones, showEjScreen, showFloodZones, showTwdbFloodHazards, showProtectedAreas, showCriticalHabitat, showWetlands, showSeismicRisk, showUrbanAreas, showWildfire, showMineSubsidence, showAmlSites,
            showDataCenters, showFederalLands, showCrownPineTracts, showAssemblyGroups, showStateTrustLands, showLandListings, showSlopeDrape, showSlope1m,
            showSegra, showZayo, showFiberLight, showNtiaMiddleMile, showFiberRowPipelines, showIxpFacilities,
          ].filter(Boolean).length}
          onResetAllLayers={handleResetAllLayers}
          showSlopeDrape={showSlopeDrape}
          onToggleSlopeDrape={() => setShowSlopeDrape(!showSlopeDrape)}
          showSlope1m={showSlope1m}
          onToggleSlope1m={() => setShowSlope1m(!showSlope1m)}
          slopeDrapeOpacity={slopeDrapeOpacity}
          onSlopeDrapeOpacityChange={setSlopeDrapeOpacity}
          showEpaZones={showEpaZones}
          onToggleEpaZones={() => setShowEpaZones(!showEpaZones)}
          epaZoneLoading={epaZoneLoading}
          showEjScreen={showEjScreen}
          onToggleEjScreen={() => setShowEjScreen(!showEjScreen)}
          ejScreenLoading={ejScreenLoading}
          showCriticalHabitat={showCriticalHabitat}
          onToggleCriticalHabitat={() => setShowCriticalHabitat(!showCriticalHabitat)}
          showWetlands={showWetlands}
          onToggleWetlands={() => setShowWetlands(!showWetlands)}
          showSeismicRisk={showSeismicRisk}
          onToggleSeismicRisk={() => setShowSeismicRisk(!showSeismicRisk)}
          showUrbanAreas={showUrbanAreas}
          onToggleUrbanAreas={() => setShowUrbanAreas(!showUrbanAreas)}
          showWildfire={showWildfire}
          onToggleWildfire={() => setShowWildfire(!showWildfire)}
          wildfireLoading={wildfireLoading}
          showMineSubsidence={showMineSubsidence}
          onToggleMineSubsidence={() => setShowMineSubsidence(!showMineSubsidence)}
          showAmlSites={showAmlSites}
          onToggleAmlSites={() => setShowAmlSites(!showAmlSites)}
        />

        {/* Center: map or list */}
        <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {assemblyMapMode && selectedAssemblyId && activeTab === 'map' && (
            <button
              onClick={() => { setAssemblyMapMode(false); setActiveTab('assemblies'); }}
              style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                zIndex: 10, padding: '6px 16px', background: '#C4705A', color: '#FFFFFF',
                border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                cursor: 'pointer', fontFamily: '"DM Sans", sans-serif',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              &larr; Back to Assembly
            </button>
          )}
          {activeTab === 'list' ? (
            <ListView filters={filters} onSelectParcel={handleSelectParcel} />
          ) : (
            <MapView
              filters={filters}
              pipelineFilters={pipelineFilters}
              selectedParcelId={selectedParcelId}
              show3DTerrain={show3DTerrain}
              onTerrainChange={setShow3DTerrain}
              onSelectParcel={handleSelectParcel}
              onSelectPipeline={handleSelectPipeline}
              onSelectCarrierFiber={handleSelectCarrierFiber}
              onSelectRailroad={handleSelectRailroad}
              onParcelCountChange={setParcelCount}
              onParcelLoadingChange={setParcelLoading}
              showParcels={showParcels}
              showFederalLands={showFederalLands}
              showCrownPineTracts={showCrownPineTracts}
              showSavedLocations={showSavedLocations}
              savedLocations={savedLocations.locations}
              onSaveLocation={savedLocations.save}
              pendingSavedFlyTo={pendingSavedFlyTo}
              onSavedFlyToHandled={() => setPendingSavedFlyTo(null)}
              showAssemblyGroups={showAssemblyGroups}
              showGathering={showGathering}
              showGasTransmission={showGasTransmission}
              showLiquidsTransmission={showLiquidsTransmission}
              showDesertSw={showDesertSw}
              pendingFitBounds={pendingFitBounds}
              onFitBoundsConsumed={() => setPendingFitBounds(null)}
              onOperatorsLoaded={setOperators}
              companyLabelFor={companyLabelFor}
              hiddenOperators={hiddenOperatorsArray}
              onPipelineCountChange={setPipelineCount}
              showPowerInfra={showPowerInfra}
              onPowerLoadingChange={setPowerLoading}
              showOsmSwitchyards={showOsmSwitchyards}
              osmSwitchyardsHifldNewOnly={osmSwitchyardsHifldNewOnly}
              onOsmSwitchyardsLoadingChange={setOsmSwitchyardsLoading}
              onSelectOsmSwitchyard={handleSelectOsmSwitchyard}
              showGridCapacity={showGridCapacity}
              onGridCapacityLoadingChange={setGridCapacityLoading}
              gridCapacityTiers={gridCapacityTiers}
              onSelectSubstation={handleSelectSubstation}
              onSelectAirport={handleSelectAirport}
              showQueueHeatmap={showQueueHeatmap}
              onQueueHeatmapLoadingChange={setQueueHeatmapLoading}
              onSelectQueueCounty={handleSelectQueueCounty}
              showCongestionHeatmap={showCongestionHeatmap}
              onCongestionHeatmapLoadingChange={setCongestionHeatmapLoading}
              onSelectCongestionCounty={handleSelectCongestionCounty}
              showElectricTerritories={showElectricTerritories}
              onSelectElectricTerritory={handleSelectElectricTerritory}
              showAirportZones={showAirportZones}
              onAirportLoadingChange={setAirportLoading}
              airportFilter={airportFilter}
              showFiber={showFiber}
              onFiberLoadingChange={setFiberLoading}
              onFiberCountChange={setFiberCount}
              showSegra={showSegra}
              showZayo={showZayo}
              showFiberLight={showFiberLight}
              showNtiaMiddleMile={showNtiaMiddleMile}
              showFiberRowPipelines={showFiberRowPipelines}
              showIxpFacilities={showIxpFacilities}
              showWater={showWater}
              onWaterLoadingChange={setWaterLoading}
              showFloodZones={showFloodZones}
              onFloodZoneLoadingChange={setFloodZoneLoading}
              onSelectFloodZone={handleSelectFloodZone}
              showTwdbFloodHazards={showTwdbFloodHazards}
              onTwdbFloodHazardsLoadingChange={setTwdbFloodHazardsLoading}
              onSelectMineSubsidence={handleSelectMineSubsidence}
              onSelectAmlSite={handleSelectAmlSite}
              onSelectWildfireCounty={handleSelectWildfireCounty}
              onSelectUrbanArea={handleSelectUrbanArea}
              onSelectSeismicPoint={handleSelectSeismicPoint}
              onSelectGasInfra={handleSelectGasInfra}
              onSelectIxpFacility={handleSelectIxpFacility}
              onSelectWaterRegZone={handleSelectWaterRegZone}
              onSelectBrackishZone={handleSelectBrackishZone}
              onSelectTdsSample={handleSelectTdsSample}
              onSelectWaterWell={handleSelectWaterWell}
              showHighways={showHighways}
              onHighwayLoadingChange={setHighwayLoading}
              showRoads={showRoads}
              onRoadsLoadingChange={setRoadsLoading}
              showOsmPipelineCandidates={showOsmPipelineCandidates}
              onOsmPipelineCandidatesLoadingChange={setOsmPipelineCandidatesLoading}
              memphisLayers={memphisLayers}
              etLayers={etLayers}
              etData={etData}
              showGeorgiaSites={showGeorgiaSites}
              showGeorgiaEvidence={showGeorgiaEvidence}
              showRailroads={showRailroads}
              onRailroadLoadingChange={setRailroadLoading}
              showDataCenters={showDataCenters}
              onDataCenterLoadingChange={setDcLoading}
              onSelectDataCenter={handleSelectDataCenter}
              showGasWells={showGasWells}
              showOilWells={showOilWells}
              onWellsLoadingChange={setWellsLoading}
              onWellsCountChange={setWellCount}
              onSelectWell={handleSelectWell}
              showStateTrustLands={showStateTrustLands}
              onTrustLandLoadingChange={setTrustLandLoading}
              showProtectedAreas={showProtectedAreas}
              showOperatorAois={showOperatorAois}
              showLandListings={showLandListings}
              onListingsLoadingChange={setListingsLoading}
              showGridListings={showGridListings}
              onGridListingsLoadingChange={setGridListingsLoading}
              onSelectListing={handleSelectListing}
              showGasPlants={showGasPlants}
              onGasPlantLoadingChange={setGasPlantLoading}
              showCompressors={showCompressors}
              showReceiptDelivery={showReceiptDelivery}
              showInterconnects={showInterconnects}
              showGasStorage={showGasStorage}
              onGasInfraLoadingChange={setGasInfraLoading}
              showEpaZones={showEpaZones}
              onEpaZoneLoadingChange={setEpaZoneLoading}
              showEjScreen={showEjScreen}
              onEjScreenLoadingChange={setEjScreenLoading}
              showCriticalHabitat={showCriticalHabitat}
              showWetlands={showWetlands}
              showWaterRegulatoryZones={showWaterRegulatoryZones}
              showTwdbWells={showTwdbWells}
              showBrackish={showBrackish}
              showTdsSamples={showTdsSamples}
              showSeismicRisk={showSeismicRisk}
              showUrbanAreas={showUrbanAreas}
              showWildfire={showWildfire}
              onWildfireLoadingChange={setWildfireLoading}
              showMineSubsidence={showMineSubsidence}
              showAmlSites={showAmlSites}
              parcelCount={parcelCount}
              pipelineCount={pipelineCount}
              wellCount={wellCount}
              fiberCount={fiberCount}
              measurementActive={measurementActive}
              onMeasurementActiveChange={armMeasurement}
              areaActive={areaActive}
              onAreaActiveChange={armArea}
              showSlopeDrape={showSlopeDrape}
              showSlope1m={showSlope1m}
              slopeDrapeOpacity={slopeDrapeOpacity}
              isSatellite={isSatellite}
              onToggleSatellite={() => setIsSatellite(!isSatellite)}
              showCountyLines={showCountyLines}
              onToggleCountyLines={() => setShowCountyLines(!showCountyLines)}
              onCursorMove={setCursorCoords}
              onZoomChange={setMapZoom}
              selectedParcelCounty={selectedParcelCounty}
              pendingCountyZoom={pendingCountyZoom}
              onCountyZoomConsumed={() => setPendingCountyZoom(null)}
              pendingFlyTo={pendingFlyTo}
              onFlyToConsumed={() => setPendingFlyTo(null)}
              searchPin={searchPin}
              onSearchPinClear={() => setSearchPin(null)}
            />
          )}
        </div>

        {/* Zone briefing overlay (URL hash routed: #/zones/<slug>) */}
        {selectedZoneSlug && (
          <ZoneBriefing
            slug={selectedZoneSlug}
            onClose={() => { window.location.hash = ''; setSelectedZoneSlug(null); }}
            onParcelClick={setSelectedParcelId}
          />
        )}

        {/* Right panel: parcel, pipeline, or well detail.
            When a zone briefing is open (z-index: 5 fixed overlay), wrap ParcelDetail
            in a fixed-position layer so pin/row clicks visibly stack on top. */}
        {selectedParcelId && (() => {
          const panel = (
            <ParcelDetail
              parcelId={selectedParcelId}
              onClose={() => { setSelectedParcelId(null); setSelectedParcelCounty(null); }}
              onParcelCounty={setSelectedParcelCounty}
              terrainEnabled={show3DTerrain}
              onShowTerrain={(center) => {
                setActiveTab('map');
                setShow3DTerrain(enabled => !enabled);
                if (center && center.every(Number.isFinite) && (!show3DTerrain || activeTab === 'list')) setPendingFlyTo(center);
                if (selectedZoneSlug) {
                  window.location.hash = '';
                  setSelectedZoneSlug(null);
                }
              }}
              savedLocation={savedLocations.findBySource('parcel', selectedParcelId!)}
              onSaveLocation={savedLocations.save}
              onDeleteSavedLocation={savedLocations.remove}
            />
          );
          return selectedZoneSlug
            ? <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 6, display: 'flex' }}>{panel}</div>
            : panel;
        })()}
        {selectedPipeline && !selectedParcelId && (
          <PipelineDetail pipeline={selectedPipeline} company={companyLabelFor(selectedPipeline.operator)} onClose={() => setSelectedPipeline(null)} onZoomToFacility={(lng, lat) => setPendingFlyTo([lng, lat])} />
        )}
        {selectedWell && !selectedParcelId && !selectedPipeline && !selectedDataCenter && (
          <WellDetail
            wellId={selectedWell.id}
            flaringMcfDay={selectedWell.flaringMcfDay}
            onClose={() => setSelectedWell(null)}
            savedLocation={savedLocations.findBySource('well', String(selectedWell.id))}
            onSaveLocation={savedLocations.save}
            onDeleteSavedLocation={savedLocations.remove}
          />
        )}
        {selectedDataCenter && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedListing && (
          <DataCenterDetail dataCenter={selectedDataCenter} onClose={() => setSelectedDataCenter(null)} />
        )}
        {selectedListing && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && (
          <LandListingDetail listing={selectedListing} onClose={() => setSelectedListing(null)} />
        )}
        {selectedFloodZone && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && (
          <FloodZoneDetail floodZone={selectedFloodZone} onClose={() => setSelectedFloodZone(null)} />
        )}
        {selectedMineSubsidence && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedFloodZone && (
          <MineSubsidenceDetail zone={selectedMineSubsidence} onClose={() => setSelectedMineSubsidence(null)} />
        )}
        {selectedAmlSite && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedFloodZone && !selectedMineSubsidence && (
          <AmlSiteDetail site={selectedAmlSite} onClose={() => setSelectedAmlSite(null)} />
        )}
        {selectedWildfireCounty && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedFloodZone && !selectedMineSubsidence && !selectedAmlSite && (
          <WildfireDetail county={selectedWildfireCounty} onClose={() => setSelectedWildfireCounty(null)} />
        )}
        {selectedUrbanArea && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedFloodZone && !selectedMineSubsidence && !selectedAmlSite && !selectedWildfireCounty && (
          <UrbanAreaDetail area={selectedUrbanArea} onClose={() => setSelectedUrbanArea(null)} />
        )}
        {selectedSeismicPoint && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedFloodZone && !selectedMineSubsidence && !selectedAmlSite && !selectedWildfireCounty && !selectedUrbanArea && (
          <SeismicRiskDetail point={selectedSeismicPoint} onClose={() => setSelectedSeismicPoint(null)} />
        )}
        {selectedGasInfra && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && (
          <GasInfraDetail facility={selectedGasInfra} onClose={() => setSelectedGasInfra(null)} />
        )}
        {selectedSubstation && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && (
          <SubstationDetail substation={selectedSubstation} onClose={() => setSelectedSubstation(null)} />
        )}
        {selectedAirport && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedSubstation && (
          <AirportDetail airport={selectedAirport} onClose={() => setSelectedAirport(null)} />
        )}
        {selectedOsmSwitchyard && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedSubstation && (
          <OsmSwitchyardDetail switchyard={selectedOsmSwitchyard} onClose={() => setSelectedOsmSwitchyard(null)} />
        )}
        {selectedQueueCounty && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedSubstation && (
          <QueueCongestionDetail data={selectedQueueCounty} onClose={() => setSelectedQueueCounty(null)} />
        )}
        {selectedCongestionCounty && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedSubstation && !selectedQueueCounty && (
          <TransmissionCongestionDetail data={selectedCongestionCounty} onClose={() => setSelectedCongestionCounty(null)} />
        )}
        {selectedElectricTerritory && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedSubstation && !selectedQueueCounty && !selectedCongestionCounty && (
          <ElectricTerritoryDetail territory={selectedElectricTerritory} onClose={() => setSelectedElectricTerritory(null)} />
        )}
        {selectedWaterRegZone && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && (
          <WaterRegZoneDetail zone={selectedWaterRegZone} onClose={() => setSelectedWaterRegZone(null)} />
        )}
        {selectedBrackishZone && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && (
          <BrackishZoneDetail zone={selectedBrackishZone} onClose={() => setSelectedBrackishZone(null)} />
        )}
        {selectedTdsSample && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && !selectedBrackishZone && (
          <TdsSampleDetail sample={selectedTdsSample} onClose={() => setSelectedTdsSample(null)} />
        )}
        {selectedWaterWell && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && !selectedBrackishZone && !selectedTdsSample && (
          <WaterWellDetail well={selectedWaterWell} onClose={() => setSelectedWaterWell(null)} />
        )}
        {selectedCarrierFiber && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && !selectedBrackishZone && !selectedTdsSample && !selectedWaterWell && !selectedIxpFacility && (
          <CarrierFiberRouteDetail carrier={selectedCarrierFiber} onClose={() => setSelectedCarrierFiber(null)} />
        )}
        {selectedIxpFacility && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && !selectedBrackishZone && !selectedTdsSample && !selectedWaterWell && !selectedCarrierFiber && (
          <IxpFacilityDetail facility={selectedIxpFacility} onClose={() => setSelectedIxpFacility(null)} />
        )}
        {selectedRailroad && !selectedParcelId && !selectedPipeline && !selectedWell && !selectedDataCenter && !selectedListing && !selectedGasInfra && !selectedWaterRegZone && !selectedBrackishZone && !selectedTdsSample && !selectedWaterWell && !selectedCarrierFiber && !selectedIxpFacility && (
          <RailroadDetail railroad={selectedRailroad} onClose={() => setSelectedRailroad(null)} />
        )}
      </div>

    </div>
  );
};

// Top toolbar component
const TABS: { key: ViewTab; label: string }[] = [
  { key: 'map', label: 'Map' },
  { key: 'list', label: 'List' },
  { key: 'counties', label: 'Counties' },
  { key: 'assemblies', label: 'Assemblies' },
];

const Toolbar: React.FC<{
  activeTab: ViewTab;
  onTabChange: (tab: ViewTab) => void;
  activeZoneSlug?: string | null;
  cursorCoords: { lat: number; lng: number } | null;
  mapZoom: number | null;
  onSearch?: (query: string) => Promise<string | null>;
}> = ({ activeTab, onTabChange, activeZoneSlug, cursorCoords, mapZoom, onSearch }) => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { logout, role } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchState, setSearchState] = useState<'idle' | 'searching' | 'error'>('idle');
  const [zones, setZones] = useState<ZoneListEntry[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchZoneList(controller.signal).then(setZones).catch(() => setZones([]));
    return () => controller.abort();
  }, []);

  const handleTabClick = useCallback((key: ViewTab) => {
    if (activeZoneSlug) window.location.hash = '';
    onTabChange(key);
  }, [activeZoneSlug, onTabChange]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setSearchState('idle');
    setTimeout(() => searchRef.current?.focus(), 0);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchState('idle');
  }, []);

  const handleSearchSubmit = useCallback(async () => {
    if (!onSearch || !searchQuery.trim()) return;
    setSearchState('searching');
    const err = await onSearch(searchQuery);
    if (err) {
      setSearchState('error');
      setTimeout(() => setSearchState('idle'), 1500);
    } else {
      closeSearch();
    }
  }, [onSearch, searchQuery, closeSearch]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearchSubmit();
    if (e.key === 'Escape') closeSearch();
  }, [handleSearchSubmit, closeSearch]);

  // Global keyboard shortcut: / to open search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && !searchOpen && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [searchOpen, openSearch]);

  return (
    <div className="app-top-bar" style={{
      height: designTokens.layout.topBarHeight,
      background: isDark ? '#2E3840' : '#8A9CAA', borderBottom: `1px solid ${isDark ? theme.border : '#8A9CAA'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 16px', flexShrink: 0,
    }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <span
          onClick={() => setShowMenu(!showMenu)}
          style={{
            fontSize: '13px', fontWeight: 500, color: isDark ? '#D49880' : '#1E2830',
            letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none',
          }}
        >
          PIPELINE PROSPECTOR <span style={{ fontSize: '9px', opacity: 0.6 }}>{'\u25BE'}</span>
        </span>
        {showMenu && (
          <>
            <div
              onClick={() => setShowMenu(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            />
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: '6px',
              background: theme.background, border: `1px solid ${theme.border}`,
              borderRadius: '6px', boxShadow: designTokens.shadows.lg, zIndex: 100,
              minWidth: '140px', overflow: 'hidden',
            }}>
              {role === 'owner' && (
                <>
                  <a
                    href="/admin"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Admin
                  </a>
                  <a
                    href="/admin/pipelines"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Pipelines
                  </a>
                  <a
                    href="/portfolio/integra"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Integra Portfolio
                  </a>
                  <a
                    href="/portfolio/crownpine"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Pine Curtain — RSM
                  </a>
                  <a
                    href="/portfolio/pine-curtain-integra"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Pine Curtain — Integra
                  </a>
                  <a
                    href="/cluster/pike-keip"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    KEIP Dossier
                  </a>
                  <a
                    href="/admin/data-status"
                    style={{
                      display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                      cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
                      fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                      color: theme.text, boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    Data Status
                  </a>
                </>
              )}
              <button
                onClick={() => { setShowMenu(false); logout(); }}
                style={{
                  width: '100%', padding: '8px 14px', border: 'none', background: 'none',
                  cursor: 'pointer', textAlign: 'left',
                  fontSize: '12px', fontFamily: designTokens.typography.fontFamily.display,
                  color: theme.text,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = theme.backgroundSecondary)}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                Log out
              </button>
            </div>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {TABS.map(t => {
          const isActive = activeTab === t.key && !activeZoneSlug;
          return (
            <button
              key={t.key}
              onClick={() => handleTabClick(t.key)}
              style={{
                padding: '6px 14px', fontSize: '11px', textTransform: 'uppercase',
                letterSpacing: '0.06em', fontWeight: 500, cursor: 'pointer',
                borderRadius: '4px', border: 'none',
                fontFamily: designTokens.typography.fontFamily.display,
                color: isActive ? (isDark ? '#D49880' : '#1E2830') : (isDark ? '#8A9CAA' : '#E4E6E8'),
                background: isActive ? (isDark ? 'rgba(196,112,90,0.12)' : 'rgba(46,56,64,0.2)') : 'transparent',
              }}
            >
              {t.label}
            </button>
          );
        })}
        {zones.length > 0 && (
          <div style={{
            width: '1px', alignSelf: 'stretch', margin: '6px 8px',
            background: isDark ? '#3A4650' : 'rgba(30,40,48,0.25)',
          }} />
        )}
        {/* The Crown Pine zone chip is replaced by the Pine Curtain dropdown
            below — the two portfolios are the entry points people actually
            want, and the zone stays reachable as its third row. */}
        {zones.filter(z => !z.archived && !z.operator_name.startsWith('Crown') && !isOneOffZone(z.slug)).map(z => {
          const isActive = activeZoneSlug === z.slug;
          const label = z.operator_name.split(/\s+/)[0];
          return (
            <a
              key={z.slug}
              href={`#/zones/${z.slug}`}
              title={`${z.operator_name} — ${z.region_label}`}
              style={{
                padding: '6px 14px', fontSize: '11px', textTransform: 'uppercase',
                letterSpacing: '0.06em', fontWeight: 500, cursor: 'pointer',
                borderRadius: '4px', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center',
                fontFamily: designTokens.typography.fontFamily.display,
                color: isActive ? (isDark ? '#D49880' : '#1E2830') : (isDark ? '#8A9CAA' : '#E4E6E8'),
                background: isActive ? (isDark ? 'rgba(196,112,90,0.12)' : 'rgba(46,56,64,0.2)') : 'transparent',
              }}
            >
              {label}
            </a>
          );
        })}
        {(() => {
          const crownZone = zones.find(z => !z.archived && z.operator_name.startsWith('Crown'));
          const isActive = crownZone != null && activeZoneSlug === crownZone.slug;
          const rows: { href: string; label: string }[] = [
            { href: '/portfolio/crownpine', label: 'RSM' },
            { href: '/portfolio/pine-curtain-integra', label: 'Integra' },
            ...(crownZone ? [{ href: `#/zones/${crownZone.slug}`, label: 'Zone view' }] : []),
          ];
          return (
            <details style={{ position: 'relative' }}>
              <summary style={{
                padding: '6px 14px', fontSize: '11px', textTransform: 'uppercase',
                letterSpacing: '0.06em', fontWeight: 500, cursor: 'pointer',
                borderRadius: '4px', listStyle: 'none', userSelect: 'none',
                display: 'inline-flex', alignItems: 'center',
                fontFamily: designTokens.typography.fontFamily.display,
                color: isActive ? (isDark ? '#D49880' : '#1E2830') : (isDark ? '#8A9CAA' : '#E4E6E8'),
                background: isActive
                  ? (isDark ? 'rgba(196,112,90,0.12)' : 'rgba(46,56,64,0.2)')
                  : 'transparent',
              }}>
                Pine Curtain ▾
              </summary>
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 40, minWidth: 180,
                marginTop: '4px', padding: '6px', borderRadius: '6px',
                background: isDark ? '#1E2830' : '#2E3840',
                border: `1px solid ${isDark ? '#3A4650' : 'rgba(255,255,255,0.15)'}`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                display: 'flex', flexDirection: 'column', gap: '2px',
              }}>
                {rows.map(r => (
                  <a
                    key={r.href}
                    href={r.href}
                    onClick={(e) => { (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open'); }}
                    style={{
                      padding: '6px 10px', fontSize: '11px', borderRadius: '4px',
                      textDecoration: 'none', whiteSpace: 'nowrap',
                      fontFamily: designTokens.typography.fontFamily.display,
                      color: '#8A9CAA',
                    }}
                  >
                    {r.label}
                  </a>
                ))}
              </div>
            </details>
          );
        })()}
        {zones.some(z => z.archived) && (
          <details style={{ position: 'relative' }}>
            <summary style={{
              padding: '6px 14px', fontSize: '11px', textTransform: 'uppercase',
              letterSpacing: '0.06em', fontWeight: 500, cursor: 'pointer',
              borderRadius: '4px', listStyle: 'none', userSelect: 'none',
              display: 'inline-flex', alignItems: 'center',
              fontFamily: designTokens.typography.fontFamily.display,
              color: zones.some(z => z.archived && activeZoneSlug === z.slug)
                ? (isDark ? '#D49880' : '#1E2830')
                : (isDark ? '#8A9CAA' : '#E4E6E8'),
              background: zones.some(z => z.archived && activeZoneSlug === z.slug)
                ? (isDark ? 'rgba(196,112,90,0.12)' : 'rgba(46,56,64,0.2)')
                : 'transparent',
            }}>
              Archive ▾
            </summary>
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 40, minWidth: 240,
              marginTop: '4px', padding: '6px', borderRadius: '6px',
              background: isDark ? '#1E2830' : '#2E3840',
              border: `1px solid ${isDark ? '#3A4650' : 'rgba(255,255,255,0.15)'}`,
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
              display: 'flex', flexDirection: 'column', gap: '2px',
            }}>
              {zones.filter(z => z.archived).map(z => (
                <a
                  key={z.slug}
                  href={`#/zones/${z.slug}`}
                  title={`${z.operator_name} - ${z.region_label} (archived)`}
                  onClick={(e) => { (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open'); }}
                  style={{
                    padding: '6px 10px', fontSize: '11px', borderRadius: '4px',
                    textDecoration: 'none', whiteSpace: 'nowrap',
                    fontFamily: designTokens.typography.fontFamily.display,
                    color: activeZoneSlug === z.slug ? '#D49880' : '#8A9CAA',
                  }}
                >
                  {z.operator_name} · {z.region_label} (archived)
                </a>
              ))}
            </div>
          </details>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}>
        <span style={{
          fontFamily: designTokens.typography.fontFamily.mono,
          fontSize: '11px', color: isDark ? '#6A7E8E' : '#E4E6E8',
        }}>
          {cursorCoords
            ? `${Math.abs(cursorCoords.lat).toFixed(3)}${cursorCoords.lat >= 0 ? '\u00B0N' : '\u00B0S'}  ${Math.abs(cursorCoords.lng).toFixed(3)}${cursorCoords.lng >= 0 ? '\u00B0E' : '\u00B0W'}`
            : ''}
          {mapZoom != null && ` z${mapZoom.toFixed(1)}`}
        </span>
        {onSearch && (
          searchOpen ? (
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setSearchState('idle'); }}
              onKeyDown={handleSearchKeyDown}
              onBlur={() => { if (!searchQuery) closeSearch(); }}
              placeholder={searchState === 'error' ? 'Not found' : searchState === 'searching' ? 'Searching...' : 'lat, lng'}
              style={{
                width: '220px', padding: '5px 10px', fontSize: '11px',
                fontFamily: designTokens.typography.fontFamily.mono,
                background: isDark ? '#1E2830' : 'rgba(30,40,48,0.25)',
                color: isDark ? '#C4D1DA' : '#E4E6E8',
                border: '1px solid transparent',
                borderColor: searchState === 'error' ? '#C4705A' : 'transparent',
                borderRadius: '4px', outline: 'none',
                transition: 'border-color 0.3s',
              }}
            />
          ) : (
            <button
              onClick={openSearch}
              title="Search coordinates  (/)"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px', display: 'flex', alignItems: 'center',
                color: isDark ? '#6A7E8E' : '#E4E6E8', opacity: 0.7,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          )
        )}
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            background: isDark ? '#3A4650' : '#6A7E8E', border: isDark ? '1px solid rgba(80,98,114,0.20)' : '1px solid #506272', borderRadius: '10px',
            cursor: 'pointer', padding: '2px 8px',
            fontFamily: designTokens.typography.fontFamily.mono,
            fontSize: '11px', color: '#E4E6E8', lineHeight: '18px',
          }}
        >
          {isDark ? '\u2600 Light' : '\u263E Dark'}
        </button>
      </div>
    </div>
  );
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, role, login } = useAuth();
  const { theme } = useTheme();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: designTokens.colors.background,
        fontFamily: designTokens.typography.fontFamily.display,
      }}>
        <form onSubmit={async (e) => {
          e.preventDefault();
          const res = await login(password);
          if (!res.success) setError(res.error || 'Failed');
        }} style={{ width: 280, textAlign: 'center' }}>
          <h2 style={{ color: designTokens.colors.text, marginBottom: 16, fontSize: designTokens.typography.fontSize.lg }}>Admin Login</h2>
          <input
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            placeholder="Password"
            autoFocus
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              background: designTokens.colors.backgroundSecondary, color: designTokens.colors.text,
              border: `1px solid ${designTokens.colors.borderLight}`, borderRadius: '6px',
              fontSize: '14px', fontFamily: designTokens.typography.fontFamily.display,
              marginBottom: 10, outline: 'none',
            }}
          />
          {error && <p style={{ color: designTokens.colors.error, fontSize: '12px', marginBottom: 8 }}>{error}</p>}
          <button type="submit" style={{
            width: '100%', padding: '10px', background: designTokens.colors.brand,
            color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px',
            fontWeight: 500, cursor: 'pointer', fontFamily: designTokens.typography.fontFamily.display,
          }}>Sign In</button>
        </form>
      </div>
    );
  }

  if (role !== 'owner') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: theme.background, color: theme.text,
        fontFamily: designTokens.typography.fontFamily.display,
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 8 }}>Access denied</h2>
          <p style={{ color: theme.textSecondary }}>Owner role required.</p>
          <a href="/" style={{ color: designTokens.colors.brand, marginTop: 12, display: 'inline-block' }}>Back to app</a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const pathname = window.location.pathname;

  // Admin route
  if (pathname === '/admin') {
    return (
      <ThemeProvider>
        <AuthProvider>
          <AdminRoute>
            <AdminPage onBack={() => { window.location.href = '/'; }} />
          </AdminRoute>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  if (pathname === '/admin/pipelines') {
    return (
      <ThemeProvider>
        <AuthProvider>
          <AdminRoute>
            <PipelineInventoryPage onBack={() => { window.location.href = '/admin'; }} />
          </AdminRoute>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  if (pathname === '/admin/data-status') {
    return (
      <ThemeProvider>
        <AuthProvider>
          <AdminRoute>
            <DataStatusPage onBack={() => { window.location.href = '/admin'; }} />
          </AdminRoute>
        </AuthProvider>
      </ThemeProvider>
    );
  }

  // Portfolio dashboard route: /portfolio/{client_slug}?token=xxx
  // Single-tenant cycle review (Atwater-curated). Click row → /briefing/{parcel_id}.
  const portfolioMatch = pathname.match(/^\/portfolio\/([a-z0-9-]+)\/?$/i);
  if (portfolioMatch) {
    const clientSlug = portfolioMatch[1].toLowerCase();
    const token = new URLSearchParams(window.location.search).get('token') || undefined;
    return (
      <ThemeProvider>
        {clientSlug === 'crownpine'
          ? <CrownPinePage clientSlug={clientSlug} token={token} />
          : clientSlug === 'pine-curtain-integra'
            ? <PineCurtainIntegraPage clientSlug={clientSlug} token={token} />
            : <PortfolioPage clientSlug={clientSlug} token={token} />}
      </ThemeProvider>
    );
  }

  // Cluster report route: /cluster/{slug}
  const clusterMatch = pathname.match(/^\/cluster\/([a-z0-9-]+)\/?$/i);
  if (clusterMatch) {
    return (
      <ThemeProvider>
        <SiteClusterReport slug={clusterMatch[1]} />
      </ThemeProvider>
    );
  }

  // Briefing route v2: /briefing/{parcelId}/v2?token=xxx
  // Must come before the v1 match so the /v2 suffix isn't absorbed into parcelId.
  const briefingV2Match = pathname.match(/^\/briefing\/([^/]+)\/v2\/?$/);
  if (briefingV2Match) {
    const parcelId = briefingV2Match[1];
    const token = new URLSearchParams(window.location.search).get('token') || undefined;
    return (
      <ThemeProvider>
        <SiteBriefingV2 parcelId={parcelId} token={token} />
      </ThemeProvider>
    );
  }

  // Briefing route: /briefing/{parcelId}?token=xxx
  // Rendered outside AuthProvider — token auth handled server-side
  const briefingMatch = pathname.match(/^\/briefing\/(.+)/);
  if (briefingMatch) {
    const parcelId = briefingMatch[1];
    const token = new URLSearchParams(window.location.search).get('token') || undefined;
    return (
      <ThemeProvider>
        <SiteBriefing parcelId={parcelId} token={token} />
      </ThemeProvider>
    );
  }

  // Zone share route: /zones/{slug}?token=xxx
  // Token must be 48-hex; otherwise the live app handles /zones/<slug>
  // navigation via its hash-routed overlay (#/zones/<slug>) under the
  // authed session. Path-based /zones/<slug> with no token would currently
  // 404 in the authed app; we only catch it here when a token is present.
  const zoneShareMatch = pathname.match(/^\/zones\/([a-z0-9-]+)\/?$/i);
  if (zoneShareMatch) {
    const zoneSlug = zoneShareMatch[1].toLowerCase();
    const zoneToken = new URLSearchParams(window.location.search).get('token');
    if (zoneToken && /^[0-9a-f]{48}$/i.test(zoneToken)) {
      return (
        <ThemeProvider>
          <ZoneSharePage slug={zoneSlug} token={zoneToken} />
        </ThemeProvider>
      );
    }
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
